import { c as _c } from "react/compiler-runtime";
import type { Editor } from "@tiptap/core";
import { type ReactNode, useEffect, useReducer, useState } from "react";
import { IconType } from "react-icons";
import { LuBold, LuChevronDown, LuClipboardPaste, LuHeading1, LuHeading2, LuHeading3, LuHighlighter, LuItalic, LuList, LuListOrdered, LuQuote, LuType, LuUnderline } from "react-icons/lu";
import { Kbd } from "@/components/ui/kbd";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { pasteWithFormatting, pasteWithoutFormatting } from "./paste-clipboard";
import { FONT_SIZE_OPTIONS, HIGHLIGHT_COLOR_OPTIONS, TEXT_COLOR_OPTIONS } from "./rich-text-options";
type OpenMenu = "size" | "color" | "highlight" | "paste" | null;
const PASTE_MODES = [{
  label: "Paste",
  description: "Keep the original formatting",
  keepFormatting: true
}, {
  label: "Paste without formatting",
  description: "Text only, styled like the note",
  keepFormatting: false
}];
function ToolbarButton(t0) {
  const $ = _c(8);
  const {
    label,
    active,
    onClick,
    children
  } = t0;
  const t1 = active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-hover";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("flex h-8 min-w-8 items-center justify-center gap-0.5 rounded px-1.5 transition-colors", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== active || $[3] !== children || $[4] !== label || $[5] !== onClick || $[6] !== t2) {
    t3 = <button type="button" aria-label={label} title={label} aria-pressed={active} onMouseDown={_temp} onClick={onClick} className={t2}>{children}</button>;
    $[2] = active;
    $[3] = children;
    $[4] = label;
    $[5] = onClick;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp(e) {
  return e.preventDefault();
}
function Divider() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="mx-0.5 h-5 w-px bg-border" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconBtn(t0) {
  const $ = _c(7);
  const {
    Icon,
    label,
    active,
    onClick
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon size={17} />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== active || $[3] !== label || $[4] !== onClick || $[5] !== t1) {
    t2 = <ToolbarButton label={label} active={active} onClick={onClick}>{t1}</ToolbarButton>;
    $[2] = active;
    $[3] = label;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function BubbleToolbar(t0) {
  const $ = _c(136);
  const {
    editor
  } = t0;
  const [, forceUpdate] = useReducer(_temp2, 0);
  const [openMenu, setOpenMenu] = useState(null);
  let t1;
  if ($[0] !== editor || $[1] !== forceUpdate) {
    t1 = () => {
      const update = () => forceUpdate();
      editor.on("transaction", update);
      editor.on("selectionUpdate", update);
      return () => {
        editor.off("transaction", update);
        editor.off("selectionUpdate", update);
      };
    };
    $[0] = editor;
    $[1] = forceUpdate;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== editor) {
    t2 = [editor];
    $[3] = editor;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== editor) {
    t3 = editor.getAttributes("textStyle");
    $[5] = editor;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const textStyle = t3;
  const activeSize = textStyle.fontSize as string | undefined;
  const activeColor = textStyle.color as string | undefined;
  const activeHighlight = textStyle.backgroundColor as string | undefined;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = menu => setOpenMenu(prev => prev === menu ? null : menu);
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const toggleMenu = t4;
  let t5;
  if ($[8] !== editor) {
    t5 = async keepFormatting => {
      setOpenMenu(null);
      const pasted = keepFormatting ? await pasteWithFormatting(editor) : await pasteWithoutFormatting(editor);
      if (pasted) {
        return;
      }
      toast({
        title: "Can't read the clipboard",
        description: <>Your browser blocked clipboard access. Press{" "}<Kbd command={keepFormatting ? "V" : "Shift+V"} /> to paste{keepFormatting ? "." : " without formatting."}</>,
        variant: "error"
      });
    };
    $[8] = editor;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handlePaste = t5;
  let t6;
  if ($[10] !== editor) {
    t6 = editor.isActive("bold");
    $[10] = editor;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== editor) {
    t7 = () => editor.chain().focus().toggleBold().run();
    $[12] = editor;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <IconBtn Icon={LuBold} label="Bold" active={t6} onClick={t7} />;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== editor) {
    t9 = editor.isActive("italic");
    $[17] = editor;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== editor) {
    t10 = () => editor.chain().focus().toggleItalic().run();
    $[19] = editor;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t9) {
    t11 = <IconBtn Icon={LuItalic} label="Italic" active={t9} onClick={t10} />;
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== editor) {
    t12 = editor.isActive("underline");
    $[24] = editor;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== editor) {
    t13 = () => editor.chain().focus().toggleUnderline().run();
    $[26] = editor;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t12 || $[29] !== t13) {
    t14 = <IconBtn Icon={LuUnderline} label="Underline" active={t12} onClick={t13} />;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Divider />;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== editor) {
    t16 = editor.isActive("heading", {
      level: 1
    });
    $[32] = editor;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== editor) {
    t17 = () => editor.chain().focus().toggleHeading({
      level: 1
    }).run();
    $[34] = editor;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t16 || $[37] !== t17) {
    t18 = <IconBtn Icon={LuHeading1} label="Heading 1" active={t16} onClick={t17} />;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== editor) {
    t19 = editor.isActive("heading", {
      level: 2
    });
    $[39] = editor;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== editor) {
    t20 = () => editor.chain().focus().toggleHeading({
      level: 2
    }).run();
    $[41] = editor;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t19 || $[44] !== t20) {
    t21 = <IconBtn Icon={LuHeading2} label="Heading 2" active={t19} onClick={t20} />;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== editor) {
    t22 = editor.isActive("heading", {
      level: 3
    });
    $[46] = editor;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== editor) {
    t23 = () => editor.chain().focus().toggleHeading({
      level: 3
    }).run();
    $[48] = editor;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t22 || $[51] !== t23) {
    t24 = <IconBtn Icon={LuHeading3} label="Heading 3" active={t22} onClick={t23} />;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Divider />;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== editor) {
    t26 = editor.isActive("bulletList");
    $[54] = editor;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== editor) {
    t27 = () => editor.chain().focus().toggleBulletList().run();
    $[56] = editor;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t26 || $[59] !== t27) {
    t28 = <IconBtn Icon={LuList} label="Bulleted list" active={t26} onClick={t27} />;
    $[58] = t26;
    $[59] = t27;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== editor) {
    t29 = editor.isActive("orderedList");
    $[61] = editor;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== editor) {
    t30 = () => editor.chain().focus().toggleOrderedList().run();
    $[63] = editor;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== t29 || $[66] !== t30) {
    t31 = <IconBtn Icon={LuListOrdered} label="Numbered list" active={t29} onClick={t30} />;
    $[65] = t29;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== editor) {
    t32 = editor.isActive("blockquote");
    $[68] = editor;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== editor) {
    t33 = () => editor.chain().focus().toggleBlockquote().run();
    $[70] = editor;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t32 || $[73] !== t33) {
    t34 = <IconBtn Icon={LuQuote} label="Quote" active={t32} onClick={t33} />;
    $[72] = t32;
    $[73] = t33;
    $[74] = t34;
  } else {
    t34 = $[74];
  }
  let t35;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Divider />;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  const t36 = openMenu === "size" || !!activeSize;
  let t37;
  let t38;
  let t39;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = () => toggleMenu("size");
    t38 = <LuType size={15} />;
    t39 = <LuChevronDown size={11} />;
    $[76] = t37;
    $[77] = t38;
    $[78] = t39;
  } else {
    t37 = $[76];
    t38 = $[77];
    t39 = $[78];
  }
  let t40;
  if ($[79] !== t36) {
    t40 = <ToolbarButton label="Font size" active={t36} onClick={t37}>{t38}{t39}</ToolbarButton>;
    $[79] = t36;
    $[80] = t40;
  } else {
    t40 = $[80];
  }
  const t41 = openMenu === "color" || !!activeColor;
  let t42;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = () => toggleMenu("color");
    $[81] = t42;
  } else {
    t42 = $[81];
  }
  let t43;
  if ($[82] !== activeColor) {
    t43 = activeColor ? {
      color: activeColor
    } : undefined;
    $[82] = activeColor;
    $[83] = t43;
  } else {
    t43 = $[83];
  }
  let t44;
  if ($[84] !== t43) {
    t44 = <span className="flex size-4 items-center justify-center rounded-sm text-caption-strong font-semibold leading-none" style={t43}>A</span>;
    $[84] = t43;
    $[85] = t44;
  } else {
    t44 = $[85];
  }
  let t45;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <LuChevronDown size={11} />;
    $[86] = t45;
  } else {
    t45 = $[86];
  }
  let t46;
  if ($[87] !== t41 || $[88] !== t44) {
    t46 = <ToolbarButton label="Text color" active={t41} onClick={t42}>{t44}{t45}</ToolbarButton>;
    $[87] = t41;
    $[88] = t44;
    $[89] = t46;
  } else {
    t46 = $[89];
  }
  const t47 = openMenu === "highlight" || !!activeHighlight;
  let t48;
  let t49;
  let t50;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = () => toggleMenu("highlight");
    t49 = <LuHighlighter size={15} />;
    t50 = <LuChevronDown size={11} />;
    $[90] = t48;
    $[91] = t49;
    $[92] = t50;
  } else {
    t48 = $[90];
    t49 = $[91];
    t50 = $[92];
  }
  let t51;
  if ($[93] !== t47) {
    t51 = <ToolbarButton label="Highlight" active={t47} onClick={t48}>{t49}{t50}</ToolbarButton>;
    $[93] = t47;
    $[94] = t51;
  } else {
    t51 = $[94];
  }
  let t52;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <Divider />;
    $[95] = t52;
  } else {
    t52 = $[95];
  }
  const t53 = openMenu === "paste";
  let t54;
  let t55;
  let t56;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = () => toggleMenu("paste");
    t55 = <LuClipboardPaste size={15} />;
    t56 = <LuChevronDown size={11} />;
    $[96] = t54;
    $[97] = t55;
    $[98] = t56;
  } else {
    t54 = $[96];
    t55 = $[97];
    t56 = $[98];
  }
  let t57;
  if ($[99] !== t53) {
    t57 = <ToolbarButton label="Paste" active={t53} onClick={t54}>{t55}{t56}</ToolbarButton>;
    $[99] = t53;
    $[100] = t57;
  } else {
    t57 = $[100];
  }
  let t58;
  if ($[101] !== t11 || $[102] !== t14 || $[103] !== t18 || $[104] !== t21 || $[105] !== t24 || $[106] !== t28 || $[107] !== t31 || $[108] !== t34 || $[109] !== t40 || $[110] !== t46 || $[111] !== t51 || $[112] !== t57 || $[113] !== t8) {
    t58 = <div className="flex items-center gap-0.5 p-1">{t8}{t11}{t14}{t15}{t18}{t21}{t24}{t25}{t28}{t31}{t34}{t35}{t40}{t46}{t51}{t52}{t57}</div>;
    $[101] = t11;
    $[102] = t14;
    $[103] = t18;
    $[104] = t21;
    $[105] = t24;
    $[106] = t28;
    $[107] = t31;
    $[108] = t34;
    $[109] = t40;
    $[110] = t46;
    $[111] = t51;
    $[112] = t57;
    $[113] = t8;
    $[114] = t58;
  } else {
    t58 = $[114];
  }
  let t59;
  if ($[115] !== handlePaste || $[116] !== openMenu) {
    t59 = openMenu === "paste" && <div className="flex flex-col border-t border-border p-1">{PASTE_MODES.map(t60 => {
        const {
          label,
          description,
          keepFormatting: keepFormatting_0
        } = t60;
        return <button key={label} type="button" onMouseDown={_temp3} onClick={() => handlePaste(keepFormatting_0)} className="flex flex-col rounded px-2 py-1 text-left text-foreground transition-colors hover:bg-hover"><span className="text-caption-strong leading-tight">{label}</span><span className="text-caption leading-tight text-muted-foreground">{description}</span></button>;
      })}</div>;
    $[115] = handlePaste;
    $[116] = openMenu;
    $[117] = t59;
  } else {
    t59 = $[117];
  }
  let t60;
  if ($[118] !== activeSize || $[119] !== editor || $[120] !== openMenu) {
    t60 = openMenu === "size" && <div className="flex max-w-[16rem] flex-wrap items-center gap-0.5 border-t border-border p-1">{FONT_SIZE_OPTIONS.map(t61 => {
        const {
          label: label_0,
          value
        } = t61;
        const active = (activeSize ?? undefined) === value;
        return <ToolbarButton key={label_0} label={label_0} active={active} onClick={() => {
          const chain = editor.chain().focus();
          if (value) {
            chain.setFontSize(value).run();
          } else {
            chain.unsetFontSize().run();
          }
          setOpenMenu(null);
        }}><span className="px-1 text-caption">{label_0}</span></ToolbarButton>;
      })}</div>;
    $[118] = activeSize;
    $[119] = editor;
    $[120] = openMenu;
    $[121] = t60;
  } else {
    t60 = $[121];
  }
  let t61;
  if ($[122] !== activeColor || $[123] !== editor || $[124] !== openMenu) {
    t61 = openMenu === "color" && <div className="flex max-w-[16rem] flex-wrap items-center gap-1.5 border-t border-border p-2">{TEXT_COLOR_OPTIONS.map(t62 => {
        const {
          label: label_1,
          value: value_0
        } = t62;
        return <button key={label_1} type="button" title={label_1} aria-label={`Text color ${label_1}`} onMouseDown={_temp4} onClick={() => {
          const chain_0 = editor.chain().focus();
          if (value_0) {
            chain_0.setColor(value_0).run();
          } else {
            chain_0.unsetColor().run();
          }
          setOpenMenu(null);
        }} className={cx("flex size-7 items-center justify-center rounded-full border bg-background text-caption-strong font-semibold text-foreground", (activeColor ?? undefined) === value_0 ? "ring-2 ring-primary" : "border-border")} style={value_0 ? {
          color: value_0
        } : undefined}>A</button>;
      })}</div>;
    $[122] = activeColor;
    $[123] = editor;
    $[124] = openMenu;
    $[125] = t61;
  } else {
    t61 = $[125];
  }
  let t62;
  if ($[126] !== activeHighlight || $[127] !== editor || $[128] !== openMenu) {
    t62 = openMenu === "highlight" && <div className="flex max-w-[16rem] flex-wrap items-center gap-1.5 border-t border-border p-2">{HIGHLIGHT_COLOR_OPTIONS.map(t63 => {
        const {
          label: label_2,
          value: value_1
        } = t63;
        return <button key={label_2} type="button" title={label_2} aria-label={`Highlight ${label_2}`} onMouseDown={_temp5} onClick={() => {
          const chain_1 = editor.chain().focus();
          if (value_1) {
            chain_1.setBackgroundColor(value_1).run();
          } else {
            chain_1.unsetBackgroundColor().run();
          }
          setOpenMenu(null);
        }} className={cx("flex size-7 items-center justify-center rounded-full border text-caption-strong font-semibold text-foreground", (activeHighlight ?? undefined) === value_1 ? "ring-2 ring-primary" : "border-border")} style={{
          backgroundColor: value_1 ?? "rgb(var(--background))"
        }}>{value_1 ? "A" : "\u2014"}</button>;
      })}</div>;
    $[126] = activeHighlight;
    $[127] = editor;
    $[128] = openMenu;
    $[129] = t62;
  } else {
    t62 = $[129];
  }
  let t63;
  if ($[130] !== t58 || $[131] !== t59 || $[132] !== t60 || $[133] !== t61 || $[134] !== t62) {
    t63 = <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-background-secondary shadow-lg">{t58}{t59}{t60}{t61}{t62}</div>;
    $[130] = t58;
    $[131] = t59;
    $[132] = t60;
    $[133] = t61;
    $[134] = t62;
    $[135] = t63;
  } else {
    t63 = $[135];
  }
  return t63;
}
function _temp5(e_1) {
  return e_1.preventDefault();
}
function _temp4(e_0) {
  return e_0.preventDefault();
}
function _temp3(e) {
  return e.preventDefault();
}
function _temp2(x) {
  return x + 1;
}